// Fixed: Removed atob() decoding and head:true from queries - v0.0.1
import { supabase } from '../lib/supabase';

export interface XeroCredentials {
  id?: string;
  organizationId: string;
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface XeroToken {
  id?: string;
  organizationId: string;
  tenantId?: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
  tokenType: string;
  scopes: string[];
  isActive?: boolean;
}

export class XeroCredentialsService {
  // Cache for credentials checks to avoid excessive DB queries
  private static credentialsCache: Map<string, { hasCredentials: boolean; timestamp: number }> = new Map();
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

  /**
   * Save or update Xero credentials for a user
   */
  static async saveCredentials(credentials: XeroCredentials): Promise<{ success: boolean; data?: any; error?: string }> {
    // Invalidate cache when saving new credentials
    this.credentialsCache.delete(credentials.organizationId);
    try {
      // Store client secret as plain text (in production, use proper encryption)
      // Not using base64 encoding as Xero secrets contain special characters
      
      const { data, error } = await supabase
        .from('xero_credentials')
        .upsert({
          organization_id: credentials.organizationId,
          client_id: credentials.clientId,
          client_secret: credentials.clientSecret,
          redirect_uri: credentials.redirectUri,
          scopes: credentials.scopes,
          is_active: true,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'organization_id'
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving Xero credentials:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error: any) {
      console.error('Error saving Xero credentials:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get Xero credentials for a user
   */
  static async getCredentials(organizationId: string): Promise<XeroCredentials | null> {
    try {
      const { data, error } = await supabase
        .from('xero_credentials')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        return null;
      }

      // Get client secret - it's stored as plain text in the database
      // Your secret contains underscores and hyphens which aren't base64, so use as-is
      const clientSecret = data.client_secret;

      return {
        id: data.id,
        organizationId: data.organization_id,
        clientId: data.client_id,
        clientSecret: clientSecret,
        redirectUri: data.redirect_uri,
        scopes: data.scopes,
        isActive: data.is_active,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('Error getting Xero credentials:', error);
      return null;
    }
  }

  /**
   * Check if Xero credentials exist for an organization
   */
  static async hasCredentials(organizationId: string): Promise<boolean> {
    try {
      // Check cache first
      const cached = this.credentialsCache.get(organizationId);
      const now = Date.now();

      if (cached && (now - cached.timestamp) < this.CACHE_TTL) {
        console.log('[XeroCredentialsService] Using cached credentials check for org:', organizationId);
        return cached.hasCredentials;
      }

      // Cache miss or expired - fetch from database
      console.log('[XeroCredentialsService] Fetching credentials check from database for org:', organizationId);
      const { count, error } = await supabase
        .from('xero_credentials')
        .select('id', { count: 'exact' })
        .eq('organization_id', organizationId)
        .eq('is_active', true);

      const hasCredentials = !error && count !== null && count > 0;

      // Update cache
      this.credentialsCache.set(organizationId, {
        hasCredentials,
        timestamp: now
      });

      return hasCredentials;
    } catch (error) {
      console.error('Error checking Xero credentials:', error);
      return false;
    }
  }

  /**
   * Delete Xero credentials for an organization
   */
  static async deleteCredentials(organizationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('xero_credentials')
        .update({ is_active: false })
        .eq('organization_id', organizationId);

      if (error) {
        return { success: false, error: error.message };
      }

      // Also delete any stored tokens
      await supabase
        .from('xero_tokens')
        .update({ is_active: false })
        .eq('organization_id', organizationId);

      // Invalidate cache
      this.credentialsCache.delete(organizationId);

      return { success: true };
    } catch (error: any) {
      console.error('Error deleting Xero credentials:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Save OAuth access tokens
   */
  static async saveTokens(tokens: XeroToken): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      // Store tokens as plain text (in production, use proper encryption)
      // Tokens are JWT format and may contain special characters

      const { data, error } = await supabase
        .from('xero_tokens')
        .upsert({
          organization_id: tokens.organizationId,
          tenant_id: tokens.tenantId,
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken,
          expires_at: tokens.expiresAt.toISOString(),
          token_type: tokens.tokenType,
          scopes: tokens.scopes,
          is_active: true,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'organization_id,tenant_id'
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving Xero tokens:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error: any) {
      console.error('Error saving Xero tokens:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get OAuth access tokens
   */
  static async getTokens(organizationId: string, tenantId?: string): Promise<XeroToken | null> {
    try {
      let query = supabase
        .from('xero_tokens')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true);

      if (tenantId) {
        query = query.eq('tenant_id', tenantId);
      }

      const { data, error } = await query.single();

      if (error || !data) {
        return null;
      }

      // Get tokens - stored as plain text
      const accessToken = data.access_token;
      const refreshToken = data.refresh_token;

      return {
        id: data.id,
        organizationId: data.organization_id,
        tenantId: data.tenant_id,
        accessToken: accessToken,
        refreshToken: refreshToken,
        expiresAt: new Date(data.expires_at),
        tokenType: data.token_type,
        scopes: data.scopes,
        isActive: data.is_active,
      };
    } catch (error) {
      console.error('Error getting Xero tokens:', error);
      return null;
    }
  }

  /**
   * Check if valid tokens exist (not expired)
   */
  static async hasValidTokens(organizationId: string): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from('xero_tokens')
        .select('id', { count: 'exact' })
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString());

      return !error && count !== null && count > 0;
    } catch (error) {
      console.error('Error checking Xero tokens:', error);
      return false;
    }
  }

  /**
   * Get stored tokens for the XeroService
   */
  static async getStoredTokens(organizationId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const tokens = await this.getTokens(organizationId);
      
      if (!tokens) {
        return { success: false, error: 'No tokens found' };
      }

      return {
        success: true,
        data: {
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken,
          expires_at: tokens.expiresAt.toISOString(),
          tenant_id: tokens.tenantId
        }
      };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate Xero OAuth URL using stored credentials
   */
  static async generateAuthUrl(organizationId: string, state?: string): Promise<string | null> {
    try {
      console.log('Generating auth URL for organization:', organizationId);
      
      const credentials = await this.getCredentials(organizationId);
      if (!credentials) {
        console.error('No Xero credentials found for organization:', organizationId);
        throw new Error('No Xero credentials found');
      }

      // Use current environment's redirect URI instead of stored one
      const currentRedirectUri = `${window.location.origin}/xero/callback`;
      
      console.log('Retrieved credentials:', {
        clientId: credentials.clientId,
        storedRedirectUri: credentials.redirectUri,
        currentRedirectUri: currentRedirectUri,
        scopes: credentials.scopes,
        hasSecret: !!credentials.clientSecret
      });

      const authUrl = new URL('https://login.xero.com/identity/connect/authorize');
      
      const params = {
        response_type: 'code',
        client_id: credentials.clientId,
        redirect_uri: currentRedirectUri,
        scope: credentials.scopes.join(' '),
        state: state || `xero_${organizationId}_${Date.now()}`,
      };

      Object.entries(params).forEach(([key, value]) => {
        authUrl.searchParams.append(key, value);
      });

      const finalUrl = authUrl.toString();
      console.log('Generated auth URL:', finalUrl);
      return finalUrl;
    } catch (error) {
      console.error('Error generating Xero auth URL:', error);
      return null;
    }
  }

  /**
   * Exchange authorization code for access tokens
   */
  static async exchangeCodeForTokens(
    organizationId: string, 
    code: string, 
    state: string
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const credentials = await this.getCredentials(organizationId);
      if (!credentials) {
        return { success: false, error: 'No Xero credentials found' };
      }

      // Use current environment's redirect URI (must match the one used in auth request)
      const currentRedirectUri = `${window.location.origin}/xero/callback`;
      
      console.log('Token exchange details:', {
        organizationId,
        clientId: credentials.clientId,
        storedRedirectUri: credentials.redirectUri,
        currentRedirectUri: currentRedirectUri,
        codeLength: code?.length
      });

      let tokens, connections;

      if (window.location.hostname === 'localhost') {
        // Direct API calls for local development (no CORS issues on localhost)
        console.log('Using direct API calls for local development');
        
        // Exchange code for tokens directly
        const tokenResponse = await fetch('https://identity.xero.com/connect/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Basic ${btoa(`${credentials.clientId}:${credentials.clientSecret}`)}`
          },
          body: new URLSearchParams({
            grant_type: 'authorization_code',
            code: code,
            redirect_uri: currentRedirectUri
          })
        });

        if (!tokenResponse.ok) {
          const errorText = await tokenResponse.text();
          return { success: false, error: `Token exchange failed: ${errorText}` };
        }

        tokens = await tokenResponse.json();

        // Get tenant/connection information
        const connectionsResponse = await fetch('https://api.xero.com/connections', {
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`
          }
        });

        if (connectionsResponse.ok) {
          connections = await connectionsResponse.json();
        } else {
          connections = [];
        }

      } else {
        // Use Vercel serverless function for production
        console.log('Using Vercel serverless function for production');
        
        const proxyUrl = `${window.location.origin}/api/xero-auth`;

        const tokenResponse = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'exchangeToken',
            code: code,
            clientId: credentials.clientId,
            clientSecret: credentials.clientSecret,
            redirectUri: currentRedirectUri
          })
        });

        if (!tokenResponse.ok) {
          const errorText = await tokenResponse.text();
          return { success: false, error: `Token exchange failed: ${errorText}` };
        }

        const result = await tokenResponse.json();
        
        if (!result.success) {
          return { success: false, error: result.error };
        }

        tokens = result.tokens;
        connections = result.connections;
      }

      let tenantId = null;
      if (connections && connections.length > 0) {
        tenantId = connections[0].tenantId;
      }

      // Save tokens to database
      await this.saveTokens({
        organizationId: organizationId,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: new Date(Date.now() + tokens.expires_in * 1000),
        tenantId: tenantId,
        tokenType: tokens.token_type,
        scopes: credentials.scopes,
        isActive: true
      });

      return {
        success: true,
        data: {
          ...tokens,
          tenant_id: tenantId
        }
      };
    } catch (error: any) {
      console.error('Error exchanging code for tokens:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Refresh access tokens
   */
  static async refreshTokens(
    organizationId: string, 
    refreshToken: string
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const credentials = await this.getCredentials(organizationId);
      if (!credentials) {
        return { success: false, error: 'No Xero credentials found' };
      }

      const tokenResponse = await fetch('https://identity.xero.com/connect/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${btoa(`${credentials.clientId}:${credentials.clientSecret}`)}`
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken
        })
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        return { success: false, error: `Token refresh failed: ${errorText}` };
      }

      const tokens = await tokenResponse.json();

      // Update tokens in database
      const currentTokens = await this.getTokens(organizationId);
      if (currentTokens) {
        await this.saveTokens({
          organizationId: organizationId,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token || refreshToken, // Use new refresh token or keep existing
          expiresAt: new Date(Date.now() + tokens.expires_in * 1000),
          tenantId: currentTokens.tenantId,
          tokenType: tokens.token_type,
          scopes: currentTokens.scopes,
          isActive: true
        });
      }

      return {
        success: true,
        data: tokens
      };
    } catch (error: any) {
      console.error('Error refreshing tokens:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Validate credentials by checking they exist and are properly formatted
   */
  static validateCredentials(credentials: Partial<XeroCredentials>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!credentials.clientId || credentials.clientId.trim().length === 0) {
      errors.push('Client ID is required');
    }

    if (!credentials.clientSecret || credentials.clientSecret.trim().length === 0) {
      errors.push('Client Secret is required');
    }

    if (!credentials.redirectUri || credentials.redirectUri.trim().length === 0) {
      errors.push('Redirect URI is required');
    } else if (!credentials.redirectUri.startsWith('http')) {
      errors.push('Redirect URI must be a valid URL');
    }

    if (!credentials.scopes || credentials.scopes.length === 0) {
      errors.push('At least one scope is required');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

export default XeroCredentialsService;